/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import type { AppResultItem, HostContext, Logger, SearchAppMetadata } from '../@types';
import SearchUtils from '../utils/SearchUtils';
import { getLogger } from '../utils/SearchLogger';
import SearchHostContext from '../utils/SearchHostContext';

class AppService {
	// Declare a static instance of the class
	static instance: AppService;
	searchHostContext: HostContext;
	logger: Logger;

	constructor(searchHostContext: HostContext) {
		// If the instance not exists, intialize it
		if (!AppService.instance) {
			this.searchHostContext = searchHostContext;
			this.logger = getLogger();
			AppService.instance = this;
		}

		return AppService.instance;
	}

	// Public methods...

	// to traverse on the object : { apps: { 'app1': { id: 'app1', name: 'app1', type: 'service' } } }
	async setAppsStatusData(appsData: SearchAppMetadata): Promise<SearchAppMetadata> {
		try {
			const appPromises = Object.keys(appsData.apps).map(async (appId) => {
				appsData.apps[appId] = await this.setAppStatus(appsData.apps[appId]);
			});
			await Promise.all(appPromises);
			return appsData;
		} catch (error) {
			const loggerData = {
				error,
				appsData,
			};
			this.logger.error('App Service', 'Problem with setAppsStatusData', loggerData);
		}
	}

	// to set isLaunchable and isInstallable flags on the app object
	async setAppStatus(app: AppResultItem): Promise<AppResultItem> {
		try {
			const isAppLaunchable = await SearchUtils.checkIfHostContextCanLaunchApp({
				hostContext: this.searchHostContext,
				appMetadata: app,
			});
			if (isAppLaunchable) {
				app.isLaunchable = true;
				app.isInstallable = false;
			} else {
				const isAppEntitled = await this.isAppEntitled(app);
				if (isAppEntitled) {
					app.isLaunchable = false;
					app.isInstallable = true;
				} else {
					app.isLaunchable = false;
					app.isInstallable = false;
				}
			}
			return app;
		} catch (error) {
			const loggerData = {
				error,
				app,
			};
			this.logger.error('App Service', 'Problem with setAppStatus', loggerData);
		}
	}

	// to check if the app is entitled
	// entitled means the app is added in the user's plan
	async isAppEntitled(app: Record<string, any>, config?: { waitForWAMConnection: boolean }): Promise<boolean> {
		try {
			let isAppEntitled: boolean = false;
			const sapCode = app.id;
			const appType = app.type;
			const { waitForWAMConnection = false } = config || {};
			if (
				appType === 'Desktop' &&
				SearchHostContext.isAuthenticated() &&
				typeof this.searchHostContext.getProductCTAActions === 'function'
			) {
				const possibleCTA = await this.searchHostContext.getProductCTAActions(sapCode, {
					waitForWAMConnection,
				});
				// if the app is KCCC, then install button should not be shown
				isAppEntitled =
					sapCode.toLowerCase() === 'kccc' ? false : possibleCTA.some((cta) => cta.actionId === 'install');
			}
			return isAppEntitled;
		} catch (error) {
			this.logger.warn('App Service', 'Failed to determine app entitlement', error);
			return false;
		}
	}
}

export default AppService;
